import { useState } from 'react'
import { Card, Select, Button, Space, Empty, Table, Tag, Spin } from 'antd'
import type { TableColumnsType } from 'antd'
import {
  SearchOutlined,
  DownloadOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { hospitalsApi } from '@/api/hospitals'
import type { Hospital, HospitalUser } from '@/types/hospital'

export const HospitalPermissions = () => {
  const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Fetch hospitals for search
  const { data: hospitalsData, isLoading: hospitalsLoading } = useQuery({
    queryKey: ['hospitals-search', searchTerm],
    queryFn: () => hospitalsApi.searchHospitals(searchTerm),
    enabled: searchTerm.length > 2,
  })

  // Get selected hospital details
  const selectedHospital = hospitalsData?.data?.find((h) => h.id === selectedHospitalId)

  // Fetch users with access to selected hospital
  const { data: hospitalUsersData, isLoading: usersLoading } = useQuery({
    queryKey: ['hospital-users', selectedHospitalId],
    queryFn: () => hospitalsApi.getHospitalUsers(selectedHospitalId!),
    enabled: !!selectedHospitalId,
  })

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export users for hospital:', selectedHospitalId)
  }

  // Table columns for hospital users
  const userColumns: TableColumnsType<HospitalUser> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '30%',
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: '15%',
      render: (active: boolean) =>
        active ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Active
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Inactive
          </Tag>
        ),
    },
    {
      title: 'Permissions',
      key: 'permissions',
      width: '55%',
      render: (_, record) => {
        if (!record.permission) {
          return <Tag color="default">No Permissions</Tag>
        }
        return (
          <Space wrap>
            {record.permission.admin && <Tag color="red">Admin</Tag>}
            {record.permission.sender && <Tag color="blue">Sender</Tag>}
            {record.permission.receiver && <Tag color="green">Receiver</Tag>}
            {record.permission.readonly && <Tag color="default">Read Only</Tag>}
            {record.permission.bi && <Tag color="purple">BI</Tag>}
            {record.permission.dashboard && <Tag color="cyan">Dashboard</Tag>}
            {record.permission.teamInsights && <Tag color="orange">Team Insights</Tag>}
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1>Hospital Permissions</h1>
        <p>View and manage permissions by hospital</p>
      </div>

      {/* Hospital Search & Selection */}
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Select Hospital
            </label>
            <Space style={{ width: '100%' }}>
              <Select
                showSearch
                placeholder="Search by hospital name or city (min 3 chars)"
                style={{ width: 500 }}
                loading={hospitalsLoading}
                filterOption={false}
                onSearch={setSearchTerm}
                onChange={setSelectedHospitalId}
                value={selectedHospitalId}
                suffixIcon={<SearchOutlined />}
                optionLabelProp="label"
                options={hospitalsData?.data?.map((hospital: Hospital) => ({
                  value: hospital.id,
                  label: hospital.name,
                }))}
                optionRender={(option) => {
                  const hospital = hospitalsData?.data?.find((h: Hospital) => h.id === option.value)
                  if (!hospital) return null
                  return (
                    <div>
                      <div style={{ fontWeight: 500 }}>{hospital.name}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {hospital.city}, {hospital.state} • CMS ID: {hospital.cmsid}
                      </div>
                    </div>
                  )
                }}
                notFoundContent={
                  searchTerm.length < 3 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      Type at least 3 characters to search
                    </div>
                  ) : hospitalsLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}><Spin size="small" /></div>
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      No hospitals found
                    </div>
                  )
                }
              />
              {selectedHospital && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled
                  title="Export functionality coming soon"
                >
                  Export Users
                </Button>
              )}
            </Space>
          </div>

          {selectedHospital && (
            <div
              style={{
                padding: '12px 16px',
                background: '#f5f5f5',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <BankOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '16px' }}>{selectedHospital.name}</div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {selectedHospital.address && `${selectedHospital.address}, `}
                  {selectedHospital.city}, {selectedHospital.state} {selectedHospital.zip}
                </div>
                <div style={{ fontSize: '13px', color: '#999', marginTop: 4 }}>
                  CMS ID: {selectedHospital.cmsid}
                </div>
              </div>
            </div>
          )}
        </Space>
      </Card>

      {/* User List */}
      {selectedHospitalId ? (
        <Card>
          <Spin spinning={usersLoading}>
            {hospitalUsersData?.data && hospitalUsersData.data.length > 0 ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <h3>Users with Access ({hospitalUsersData.data.length})</h3>
                  <p style={{ color: '#666' }}>
                    Users who have permissions to access this hospital
                  </p>
                </div>
                <Table
                  columns={userColumns}
                  dataSource={hospitalUsersData.data}
                  rowKey="id"
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total ${total} users`,
                  }}
                />
              </>
            ) : !usersLoading ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No users have access to this hospital"
              >
                <Button type="primary" href="/permissions/users">
                  Grant Permissions to Users
                </Button>
              </Empty>
            ) : null}
          </Spin>
        </Card>
      ) : (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a hospital to view all users with access"
          />
        </Card>
      )}
    </div>
  )
}
